import { useMemo } from 'react';

import { atom, useAtom, useAtomValue } from 'jotai';

import { FileMetadata } from '@app/models/types/field-types';
import { useGroupItemScope } from '@app/store/jotai/group-item-scope';
import type { AddressParts } from '@app/utils/address';
import { itemScopeAnswers, writeItemAnswers } from '@app/utils/repeating-groups';
import { Invalidations } from '@app/utils/vvalidation-utils';

enum AnswerType {
    TEXT = 'text',
    CHOICE = 'choice',
    CHOICES = 'choices',
    NUMBER = 'number',
    BOOLEAN = 'boolean',
    MATRIX = 'matrix',
    EMAIL = 'email',
    DATE = 'date',
    TIME = 'time',
    CURRENCY = 'currency',
    DATE_RANGE = 'date_range',
    TAGS = 'tags',
    ADDRESS = 'address',
    SIGNATURE = 'signature',
    URL = 'url',
    PHONE_NUMBER = 'phone_number',
    FILE_URL = 'file_url',
    PAYMENT = 'payment',
    FILE_UPLOAD = 'file_upload',
    RATING = 'rating',
    LINEAR_RATING = 'linear_rating',
    TABULAR_INPUT = 'tabular_input',
    GROUP = 'group'
}

export interface ChoicesAnswer {
    values?: Array<string>;
    other?: string;
}

export interface ChoiceAnswer {
    value?: string;
    other?: string;
    /** The text of the chosen option of a question whose options come from a source (the server writes it again). */
    label?: string;
}

export interface ChoicesAnswer {
    values?: Array<string>;
    other?: string;
    /** Option id -> text, for a question whose options come from a source (the server writes it again). */
    labels?: Record<string, string>;
}

export interface FormResponse {
    formId: string;
    answers: {
        [fieldId: string]: {
            type: AnswerType;
            text?: string;
            number?: number;
            email?: string;
            date?: string;
            time?: string;
            currency?: { code?: string; amount?: string };
            date_range?: { start?: string; end?: string };
            tags?: string[];
            address?: AddressParts;
            signature?: { method?: string; file_id?: string; name?: string; signed_at?: string; url?: string };
            phone_number?: string;
            url?: string;
            choice?: ChoiceAnswer;
            choices?: ChoicesAnswer;
            boolean?: boolean;
            file_metadata?: FileMetadata;
            tabular_value?: string[][];
            // Repeating group: one answers map (child id → answer) per item.
            items?: Array<Record<string, any>>;
        };
    };
    invalidFields?: Record<string, Array<Invalidations | string>>;
    anonymize?: boolean;
}

const initialFormResponse: FormResponse = {
    formId: '',
    answers: {}
    // `anonymize` starts UNDEFINED (not false): identity sharing is opt-in
    // (DESIGN.md §5), so until the responder touches the checkbox, the
    // effective value is decided at submit time — anonymous when identity is
    // optional, attached when the form requires a verified identity.
};

const formResponseAtom = atom<FormResponse>(initialFormResponse);

/**
 * The form's own answers, also inside a repeating-group item (where
 * `useFormResponse` presents that item's answers layered over them): what
 * field rules are evaluated against.
 */
export const useRootFormAnswers = (): FormResponse['answers'] => useAtomValue(formResponseAtom).answers;

type FormResponseUpdate = FormResponse | ((previous: FormResponse) => FormResponse);

export const useFormResponse = () => {
    const [rootFormResponse, setRootFormResponse] = useAtom(formResponseAtom);
    const itemScope = useGroupItemScope();

    // Inside a repeating-group item, the setters below read and write the
    // item's answers through a scoped view (see utils/repeating-groups.ts);
    // everywhere else they work on the form's answers directly.
    const scopedView = (response: FormResponse): FormResponse => (itemScope ? { ...response, answers: itemScopeAnswers(response.answers, itemScope) as FormResponse['answers'] } : response);
    const formResponse = useMemo(() => scopedView(rootFormResponse), [rootFormResponse, itemScope]);
    const setFormResponse = (update: FormResponseUpdate) => {
        if (!itemScope) {
            setRootFormResponse(update as any);
            return;
        }
        setRootFormResponse((previous) => {
            const next = typeof update === 'function' ? update(scopedView(previous)) : update;
            return { ...previous, ...next, answers: writeItemAnswers(previous.answers, itemScope, next.answers || {}) as FormResponse['answers'] };
        });
    };

    const addFieldTextAnswer = (fieldId: string, text: string) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.TEXT,
                    text: text
                }
            }
        });
    };

    const addFieldChoiceAnswer = (fieldId: string, choice: string) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.CHOICE,
                    choice: {
                        value: choice
                    }
                }
            }
        });
    };

    /** A question whose options come from a source: the id and the text of the chosen option (the server writes the stored text again). */
    const addSourcedChoiceAnswer = (fieldId: string, id: string, label: string) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: { type: AnswerType.CHOICE, choice: { value: id, label } }
            }
        });
    };

    /** ... several options (a multiple choice), keeping an "Other" text already typed. */
    const addSourcedChoicesAnswer = (fieldId: string, chosen: Array<{ id: string; label: string }>) => {
        const other = formResponse.answers?.[fieldId]?.choices?.other;
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.CHOICES,
                    choices: {
                        values: chosen.map((option) => option.id),
                        labels: Object.fromEntries(chosen.map((option) => [option.id, option.label])),
                        ...(other ? { other } : {})
                    }
                }
            }
        });
    };

    const addFieldChoicesAnswer = (fieldId: string, choices: string[]) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.CHOICES,
                    choices: {
                        ...formResponse.answers[fieldId]?.choices,
                        values: choices
                    }
                }
            }
        });
    };

    const addOtherChoiceAnswer = (fieldId: string, other: string) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse?.answers || {}),
                [fieldId]: {
                    type: AnswerType.CHOICE,
                    choice: {
                        other
                    }
                }
            }
        });
    };

    const addOtherChoicesAnswer = (fieldId: string, other: string) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse?.answers || {}),
                [fieldId]: {
                    type: AnswerType.CHOICES,
                    choices: {
                        ...(formResponse?.answers ? formResponse?.answers[fieldId]?.choices : {}),
                        other
                    }
                }
            }
        });
    };

    const addFieldNumberAnswer = (fieldId: string, number: number) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.NUMBER,
                    number: number
                }
            }
        });
    };

    const addFieldBooleanAnswer = (fieldId: string, value: boolean) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.BOOLEAN,
                    boolean: value
                }
            }
        });
    };

    const addFieldEmailAnswer = (fieldId: string, email: string) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.EMAIL,
                    email: email
                }
            }
        });
    };

    const addFieldDateAnswer = (fieldId: string, date: string) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.DATE,
                    date: date
                }
            }
        });
    };

    const addFieldTimeAnswer = (fieldId: string, time: string) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.TIME,
                    time: time
                }
            }
        });
    };

    const addFieldCurrencyAnswer = (fieldId: string, code: string, amount: string) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.CURRENCY,
                    currency: { code, amount }
                }
            }
        });
    };

    // A date range: either end may still be empty while the respondent picks them (both are needed to submit).
    const addFieldDateRangeAnswer = (fieldId: string, start: string, end: string) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.DATE_RANGE,
                    date_range: { start, end }
                }
            }
        });
    };

    // An address: only the parts that are given are stored (a country alone is no answer: the field keeps it until a part is typed).
    const addFieldAddressAnswer = (fieldId: string, address: AddressParts) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.ADDRESS,
                    address
                }
            }
        });
    };

    const addFieldTagsAnswer = (fieldId: string, tags: string[]) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.TAGS,
                    tags
                }
            }
        });
    };

    // A signature: typed (the name) or drawn (the id of the image, which goes along as a file part of the submission).
    // From the answers as they are when it runs: a drawing is committed after the PNG is made (an await).
    const addFieldSignatureAnswer = (fieldId: string, signature: { method: 'typed'; name: string } | { method: 'drawn'; file_id: string }) => {
        setFormResponse((previous) => ({
            ...previous,
            answers: {
                ...(previous.answers || {}),
                [fieldId]: {
                    type: AnswerType.SIGNATURE,
                    signature
                }
            }
        }));
    };

    const addFieldURLAnswer = (fieldId: string, url: string) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.URL,
                    url: url
                }
            }
        });
    };

    const addFieldPhoneNumberAnswer = (fieldId: string, phoneNumber: string) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.PHONE_NUMBER,
                    phone_number: phoneNumber
                }
            }
        });
    };

    const addFieldFileAnswer = (fieldId: string, fileMetaData: FileMetadata) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.FILE_UPLOAD,
                    file_metadata: fileMetaData
                }
            }
        });
    };

    const addFieldRatingAnswer = (fieldId: string, number: number) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.RATING,
                    number: number
                }
            }
        });
    };

    const addFieldLinearRatingAnswer = (fieldId: string, number: number) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.LINEAR_RATING,
                    number: number
                }
            }
        });
    };

    const addFieldTabularAnswer = (fieldId: string, value: string[][]) => {
        setFormResponse({
            ...formResponse,
            answers: {
                ...(formResponse.answers || {}),
                [fieldId]: {
                    type: AnswerType.TABULAR_INPUT,
                    tabular_value: value
                }
            }
        });
    };

    const setInvalidFields = (invalidFields: Record<string, Array<Invalidations | string>>) => {
        setFormResponse({
            ...formResponse,
            invalidFields
        });
    };

    // A new answers object, never the old one changed: whatever reads the answers by
    // identity (the field rule states) would not see the change.
    const removeAnswer = (fieldId: string) => {
        const answers = { ...(formResponse.answers ?? {}) };
        delete answers[fieldId];
        setFormResponse({ ...formResponse, answers });
    };

    const resetFormResponseAnswer = () => {
        setFormResponse({
            formId: '',
            answers: {}
        });
    };

    return {
        formResponse,
        setFormResponse,
        addFieldTextAnswer,
        addFieldSignatureAnswer,
        addFieldBooleanAnswer,
        addFieldChoiceAnswer,
        addFieldDateAnswer,
        addFieldTimeAnswer,
        addFieldCurrencyAnswer,
        addFieldDateRangeAnswer,
        addFieldTagsAnswer,
        addFieldAddressAnswer,
        addFieldEmailAnswer,
        addFieldNumberAnswer,
        addFieldPhoneNumberAnswer,
        addFieldURLAnswer,
        addFieldChoicesAnswer,
        addSourcedChoiceAnswer,
        addSourcedChoicesAnswer,
        addOtherChoiceAnswer,
        addOtherChoicesAnswer,
        addFieldFileAnswer,
        addFieldRatingAnswer,
        addFieldLinearRatingAnswer,
        addFieldTabularAnswer,
        removeAnswer,
        setInvalidFields,
        resetFormResponseAnswer
    };
};
